import { useEffect, useState } from "react";
import { api, setTokens } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get("token") ?? "";
}

export function AcceptInvitePage() {
  const [token] = useState(tokenFromUrl);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [valid, setValid] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setValid(false);
      setError("Missing invite token");
      return;
    }
    api
      .get<{ email: string }>(`/admin/auth/invite/${token}`)
      .then(({ data }) => {
        setEmail(data.email);
        setValid(true);
      })
      .catch((err: unknown) => {
        setValid(false);
        setError(apiErrorMessage(err, "This invite is invalid or has expired"));
      });
  }, [token]);

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post<{
        accessToken: string;
        refreshToken: string;
      }>("/admin/auth/invite/accept", { token, password });
      setTokens(data.accessToken, data.refreshToken);
      window.location.replace("/");
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to set password"));
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <main style={{ width: "100%", maxWidth: 420 }}>
        <h2
          style={{
            fontFamily: "var(--f-head)",
            fontWeight: 500,
            fontSize: 28,
            margin: "0 0 8px",
          }}
        >
          Set your password
        </h2>
        <p
          style={{
            fontSize: 13,
            color: "var(--ink-mute)",
            margin: "0 0 24px",
          }}
        >
          Activate your Provenance admin account.
        </p>

        {valid === false ? (
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              color: "var(--danger)",
              border: "1px solid var(--danger)",
              padding: "10px 14px",
            }}
          >
            {error}
          </div>
        ) : valid === null ? (
          <div style={{ fontSize: 13, color: "var(--ink-mute)" }}>
            Validating invite…
          </div>
        ) : (
          <form className="form" onSubmit={handleSubmit}>
            <div className="field">
              <Label htmlFor="ai-email">Email</Label>
              <Input id="ai-email" type="email" value={email} disabled />
            </div>
            <div className="field">
              <Label htmlFor="ai-pw">Password</Label>
              <Input
                id="ai-pw"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="field">
              <Label htmlFor="ai-confirm">Confirm password</Label>
              <Input
                id="ai-confirm"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting}
              />
            </div>

            {error && (
              <div
                style={{
                  fontFamily: "var(--f-mono)",
                  fontSize: 12,
                  color: "var(--danger)",
                  border: "1px solid var(--danger)",
                  padding: "10px 14px",
                }}
              >
                {error}
              </div>
            )}

            <Button type="submit" disabled={submitting} size="lg">
              {submitting ? "Activating…" : "Activate account"}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
